import { Component, computed, input } from '@angular/core';

/** A single item in the list diff view. */
interface ListDiffItem {
  text: string;
  /**
   * Lifecycle classification:
   * - `'added'`     — item is in the current version but not in the previous version.
   * - `'removed'`   — item was in the previous version but is absent from the current version.
   * - `'unchanged'` — item appears in both versions (exact string match).
   */
  status: 'added' | 'removed' | 'unchanged';
}

/**
 * Displays a list of string items with optional compare-mode highlighting.
 *
 * In compare mode, each item is classified as added (green), removed (red strikethrough),
 * or unchanged based on exact string equality. Items are compared as opaque strings — a
 * single character difference makes two items distinct.
 *
 * Display order:
 * 1. Current items in their original order (green if new, normal if in both).
 * 2. Items that were in the previous version but are absent from the current version
 *    (red strikethrough), appended after the current items.
 *
 * Usage:
 * ```html
 * <app-revision-list-field
 *   [currentItems]="noteTexts"
 *   [previousItems]="previousNoteTexts"
 *   [isCompareMode]="isCompareMode">
 * </app-revision-list-field>
 * ```
 */
@Component({
  selector: 'app-revision-list-field',
  templateUrl: './revision-list-field.component.html',
  styleUrls: ['./revision-list-field.component.scss'],
  standalone: false
})
export class RevisionListFieldComponent {
  /** Current items to display. Each item is compared against `previousItems` as an exact string. */
  currentItems = input<string[]>([]);

  /**
   * Previous items for compare mode.
   * `undefined` means the previous version has not been fetched yet — diff highlights are
   * suppressed to avoid false positives during loading.
   * An empty array `[]` means the previous version had no items.
   */
  previousItems = input<string[] | undefined>(undefined);

  /** Whether compare mode is active. When `false`, items are shown without highlighting. */
  isCompareMode = input<boolean>(false);

  /**
   * Computed diff list used by the template.
   *
   * In normal mode: all current items with `'unchanged'` status.
   * In compare mode (when `previousItems` is defined): current items are marked `'added'` or
   * `'unchanged'`, followed by any items present only in the previous version (`'removed'`).
   * When `previousItems` is `undefined` (not yet loaded), items are shown without diff to
   * prevent false positives while the fetch is in progress.
   */
  diffItems = computed<ListDiffItem[]>(() => {
    const current: string[] = this.currentItems();

    if (!this.isCompareMode()) {
      return current.map(text => ({ text, status: 'unchanged' as const }));
    }

    const prev: string[] | undefined = this.previousItems();

    // Previous data not yet loaded — show current items without highlights
    if (prev === undefined) {
      return current.map(text => ({ text, status: 'unchanged' as const }));
    }

    const prevSet: Set<string> = new Set(prev);
    const currSet: Set<string> = new Set(current);
    const result: ListDiffItem[] = [];

    // Walk current items in order; mark as added if absent from previous, unchanged if shared
    for (const text of current) {
      result.push({ text, status: prevSet.has(text) ? 'unchanged' : 'added' });
    }

    // Append items that existed in the previous version but are no longer present
    for (const text of prev) {
      if (!currSet.has(text)) {
        result.push({ text, status: 'removed' });
      }
    }

    return result;
  });
}
